/**
 * useWinValue — Hook für WinValue UltraExpert Public API Integration
 *
 * Alle WinValue-Aufrufe laufen über die serverseitige Proxy-Route
 * /api/winvalue-sync — der API-Key verlässt nie den Server.
 *
 * Funktionen:
 *   - pushAkte: Akte von Supabase → WinValue erstellen
 *   - pullAkte: Akte von WinValue → Supabase ziehen
 *   - syncAll: Alle verknüpften Akten synchronisieren
 *   - getStatus: Sync-Status abrufen
 *   - getDossier: Einzelnes WinValue-Dossier lesen (über Server-Proxy)
 *   - createDossier: Neues WinValue-Dossier anlegen (über Server-Proxy)
 *   - deleteDossier: WinValue-Dossier löschen (über Server-Proxy)
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// Alle WinValue-Aufrufe laufen über diesen Server-Proxy
const SYNC_API = '/api/winvalue-sync';

export interface WinValueDossier {
  uid: string;
  browserUrl: string;
  dossier: {
    referenceNr?: string;
    orderType?: string;
    orderDate?: string;
    damageDate?: string;
    expertUser?: string;
  };
  vehicle: {
    licensePlate?: string;
    make?: string;
    model?: string;
    vin?: string;
    mileage?: number;
    mileageUnit?: string;
    firstRegistration?: string;
    color?: string;
  };
  participants: Array<{
    type?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    street?: string;
    zip?: string;
    city?: string;
    countryCode?: string;
    phone?: string;
    email?: string;
  }>;
  values: Record<string, any>;
  documents: Array<{
    uid?: string;
    name?: string;
    mimeType?: string;
    size?: number;
  }>;
}

export interface SyncStatus {
  winvalue_api: string;
  total_akten: number;
  synced_akten: number;
  unsynced_akten: number;
  last_sync: string | null;
}

export interface SyncResult {
  action: string;
  status: string;
  winvalue_uid?: string;
  winvalue_url?: string;
  winvalue_ref?: string;
  aktenzeichen?: string;
  akteId?: string;
  total?: number;
  synced?: number;
  errors?: number;
  results?: any[];
  fields_updated?: string[];
}

interface WinValueState {
  isLoading: boolean;
  error: string | null;
  syncStatus: SyncStatus | null;
  lastResult: SyncResult | null;
}

export function useWinValue() {
  const [state, setState] = useState<WinValueState>({
    isLoading: false,
    error: null,
    syncStatus: null,
    lastResult: null,
  });

  // ── Sync-API aufrufen ──
  const callSyncApi = useCallback(async (action: string, body?: any): Promise<any> => {
    const method = action === 'status' ? 'GET' : 'POST';
    const url = `${SYNC_API}?action=${action}`;
    const { data: { session } } = await supabase.auth.getSession();

    const opts: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    };

    const res = await fetch(url, opts);
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || `Sync-Fehler: ${res.status}`);
    }
    
    return data;
  }, []);


  // ── Akte → WinValue pushen ──
  const pushAkte = useCallback(async (akteId: string): Promise<SyncResult> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callSyncApi('push', { akteId });
      setState(s => ({ ...s, isLoading: false, lastResult: result }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── WinValue → Akte ziehen ──
  const pullAkte = useCallback(async (winvalueUid: string, akteId?: string): Promise<SyncResult> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callSyncApi('pull', { winvalueUid, akteId });
      setState(s => ({ ...s, isLoading: false, lastResult: result }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── Alle verknüpften Akten synchronisieren ──
  const syncAll = useCallback(async (): Promise<SyncResult> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callSyncApi('sync-all');
      setState(s => ({ ...s, isLoading: false, lastResult: result }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── Sync-Status abrufen ──
  const getStatus = useCallback(async (): Promise<SyncStatus> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callSyncApi('status');
      setState(s => ({ ...s, isLoading: false, syncStatus: result }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── Einzelnes WinValue-Dossier lesen (über Server-Proxy) ──
  const getDossier = useCallback(async (uid: string): Promise<WinValueDossier> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callSyncApi('get-dossier', { uid });
      setState(s => ({ ...s, isLoading: false }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── Neues Dossier anlegen (über Server-Proxy) ──
  const createDossier = useCallback(async (data: {
    orderType?: string;
    referenceNr?: string;
    vehicle?: { licensePlate?: string; make?: string; model?: string };
    participants?: Array<{ type?: string; firstName?: string; lastName?: string; email?: string; phone?: string }>;
  }): Promise<WinValueDossier> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const dossierData: any = {
        dossier: {
          orderType: data.orderType || 'DAMAGE_EXPERTISE',
          orderDate: new Date().toISOString().split('T')[0],
        },
      };
      if (data.referenceNr) dossierData.dossier.referenceNr = data.referenceNr;
      if (data.vehicle) dossierData.vehicle = { ...data.vehicle, mileageUnit: 'KILOMETERS' };
      if (data.participants) dossierData.participants = data.participants;

      const result = await callSyncApi('create-dossier', { dossierData });
      setState(s => ({ ...s, isLoading: false }));
      return result;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  // ── Dossier löschen (über Server-Proxy) ──
  const deleteDossier = useCallback(async (uid: string): Promise<void> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      await callSyncApi('delete-dossier', { uid });
      setState(s => ({ ...s, isLoading: false }));
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      throw err;
    }
  }, [callSyncApi]);

  return {
    ...state,
    pushAkte,
    pullAkte,
    syncAll,
    getStatus,
    getDossier,
    createDossier,
    deleteDossier,
  };
}
